import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Phone, Calendar, Check, Send, Star, ExternalLink, BadgeCheck, Clock, Paperclip, Download } from 'lucide-react';
import { BsWhatsapp } from 'react-icons/bs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Button } from '../Components/UI/button';
import { Textarea } from '../Components/UI/textarea';
import { Label } from '../Components/UI/label';
import { getImageRoot, formatDateFull, formatDateShort, getUploadUrl } from '../Utils/Utilities';
import { SpeechControls } from '../Components/Common/SpeechControls';
import { useCurrency } from '../Hooks/useCurrency';
import AttachmentPreviewModal from '../Components/Common/AttachmentPreviewModal';
import externalAPI from './ExternalAPI';

const PRESET_NON_PURCHASE_REASONS = [
  { id: 'budget', label: 'Pricing is higher than budget' },
  { id: 'competitor', label: 'Chose another vendor' },
  { id: 'requirements', label: "Product doesn't fit our requirements" },
  { id: 'timing', label: 'Project postponed or cancelled' },
  { id: 'other', label: 'Other reason' },
];

const RATINGS_FEEDBACK_CONFIG = [
  {
    score: 1,
    label: 'Very Bad',
    color: 'sahi-red',
    colorClass: 'text-sahi-red',
    fillClass: 'fill-sahi-red text-sahi-red'
  },
  {
    score: 2,
    label: 'Bad',
    color: 'sahi-orange',
    colorClass: 'text-sahi-orange',
    fillClass: 'fill-sahi-orange text-sahi-orange'
  },
  {
    score: 3,
    label: 'Okay',
    color: 'sahi-dark-blue',
    colorClass: 'text-sahi-dark-blue',
    fillClass: 'fill-sahi-dark-blue text-sahi-dark-blue'
  },
  {
    score: 4,
    label: 'Good',
    color: 'sahi-blue',
    colorClass: 'text-sahi-blue',
    fillClass: 'fill-sahi-blue text-sahi-blue'
  },
  {
    score: 5,
    label: 'Excellent',
    color: 'sahi-green',
    colorClass: 'text-sahi-green',
    fillClass: 'fill-sahi-green text-sahi-green'
  }
];

const parseRejectionReasons = (rawRsns, typeID) => {
  if (!rawRsns) return [];
  try {
    const masterData = typeof rawRsns === 'string' ? JSON.parse(rawRsns) : rawRsns;
    if (!masterData) return [];

    if (Array.isArray(masterData)) {
      const hasTypeWrappers = masterData.some(item => item && (item.TID !== undefined || item.Rsns !== undefined));
      if (hasTypeWrappers) {
        const targetTID = 1;
        let matched = masterData.find(item => Number(item?.TID) === targetTID);
        if (!matched && typeID) {
          matched = masterData.find(item => Number(item?.TID) === Number(typeID));
        }
        if (!matched && masterData.length > 0) {
          matched = masterData[0];
        }

        if (!matched || !matched.Rsns) return [];
        const reasons = typeof matched.Rsns === 'string' ? JSON.parse(matched.Rsns) : matched.Rsns;
        if (!Array.isArray(reasons)) return [];

        return reasons.map((reason, index) => {
          const label = reason?.R || reason?.label || reason?.name || reason?.Reason || String(reason);
          return {
            id: String(reason?.RID || reason?.id || index + 1),
            label: label
          };
        });
      }

      return masterData.map((reason, index) => {
        const label = reason?.R || reason?.label || reason?.name || reason?.Reason || String(reason);
        return {
          id: String(reason?.RID || reason?.id || index + 1),
          label: label
        };
      });
    }

    const directRsns = masterData.Rsns || masterData.reasons;
    if (directRsns) {
      const reasons = typeof directRsns === 'string' ? JSON.parse(directRsns) : directRsns;
      if (Array.isArray(reasons)) {
        return reasons.map((reason, index) => {
          const label = reason?.R || reason?.label || reason?.name || reason?.Reason || String(reason);
          return {
            id: String(reason?.RID || reason?.id || index + 1),
            label: label
          };
        });
      }
    }

    return [];
  } catch (error) {
    console.error('Error parsing rejection reasons:', error);
    return [];
  }
};

const OrderStatus = () => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(useLocation().search);
  const tid = searchParams.get('tid');
  const aid = searchParams.get('aid');
  const ratingParam = searchParams.get('rating') || searchParams.get('r');

  const [supplier, setSupplier] = useState({
    name: "",
    tagline: "",
    phone: "",
    whatsapp: "",
    email: "",
    logo: "",
    logoText: "",
    countryID: null,
    googleLink: "",
    rejRsns: ""
  });

  const [order, setOrder] = useState({
    number: tid || '',
    connID: null,
    typeID: null,
    statusID: null,
    contactName: '',
    bizName: '',
    amount: '',
    status: '',
    item: '',
    dueDT: null,
    doneDT: null,
    createdDT: null,
    timeline: [],
    fdbkScore: 0,
    fdbkNotes: '',
    rejReason: '',
    thrFile: ''
  });

  // Preview modal state
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Message state (for ongoing/active orders)
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sentSuccess, setSentSuccess] = useState(false);

  // Feedback state (for completed orders, StatusID === 2)
  const [rating, setRating] = useState(Number(ratingParam) || 0);
  const [hoverRating, setHoverRating] = useState(0);
  const [selectedReason, setSelectedReason] = useState('');
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);
  const [hasExistingFeedback, setHasExistingFeedback] = useState(false);

  const currencySymbol = useCurrency(supplier.countryID || null);

  const failureReasonsList = useMemo(() => {
    const parsed = parseRejectionReasons(supplier.rejRsns, order.typeID);
    if (parsed && parsed.length > 0) {
      return parsed;
    }
    return PRESET_NON_PURCHASE_REASONS;
  }, [supplier.rejRsns, order.typeID]);

  useEffect(() => {
    if (!tid) {
      navigate('/', { replace: true });
      return;
    }

    if (!externalAPI?.getFeedbackDetails) return;
    (async () => {
      try {
        const res = await externalAPI.getFeedbackDetails({ agencyID: aid, threadID: tid }, 'ORD');
        const agency = res?.parsedDataSet?.Table1?.[0];
        const ord = res?.parsedDataSet?.Table2?.[0];

        if (agency) {
          const rawMobile = agency.Mobile ? String(agency.Mobile).replace(/\D/g, '') : '';
          const name = agency.AgencyName || '';
          setSupplier({
            name: name,
            tagline: agency.Tagline || '',
            phone: agency.Mobile || '',
            whatsapp: rawMobile ? (rawMobile.startsWith('91') || rawMobile.length > 10 ? rawMobile : `91${rawMobile}`) : '',
            email: agency.Email || '',
            logo: agency.Logo ? (agency.Logo.startsWith('http') ? agency.Logo : getUploadUrl(`Uploads/${agency.Logo}`)) : '',
            logoText: name ? name.slice(0, 2).toUpperCase() : 'SS',
            countryID: agency.CountryID || null,
            googleLink: agency.GoogleLink || '',
            rejRsns: agency.RejRsns || ''
          });
        }

        if (ord) {
          let timeline = [];
          let currentStatus = '';

          try {
            const rawHist = ord.StatusHist;
            const parsedHist = typeof rawHist === 'string' ? JSON.parse(rawHist) : (Array.isArray(rawHist) ? rawHist : []);
            const visibleHist = parsedHist.filter(item => Number(item?.Show) === 1 || item?.Show === true);

            if (visibleHist.length > 0) {
              timeline = visibleHist.map((item, idx) => {
                const isLast = idx === visibleHist.length - 1;
                return {
                  title: item.S || item.Status || '',
                  date: item.T ? formatDateShort(item.T) : '',
                  subtitle: item.D || '',
                  status: isLast ? 'active' : 'completed'
                };
              });
              currentStatus = visibleHist[visibleHist.length - 1]?.S || visibleHist[visibleHist.length - 1]?.Status || '';
            }
          } catch (e) {
            console.error("Error parsing StatusHist:", e);
          }

          const ordStatusID = ord.StatusID != null ? Number(ord.StatusID) : (ord.DoneDT ? 2 : null);

          setOrder(prev => ({
            ...prev,
            number: ord.Reference || tid,
            connID: ord.ConnID ?? prev.connID,
            typeID: ord.TypeID ?? prev.typeID,
            statusID: ordStatusID,
            contactName: ord.ContactName || ord.ContactPerson || prev.contactName,
            bizName: ord.BizName || prev.bizName,
            amount: ord.BizProspect != null ? Number(ord.BizProspect).toLocaleString('en-IN') : prev.amount,
            item: ord.MainProduct || prev.item,
            status: currentStatus || ord.CurrStage || prev.status,
            dueDT: ord.DueDT,
            doneDT: ord.DoneDT,
            createdDT: ord.CreatedDT || ord.SinceDT,
            timeline: timeline.length > 0 ? timeline : prev.timeline,
            fdbkScore: ord.FdbkScore || 0,
            fdbkNotes: ord.FdbkNotes || '',
            rejReason: ord.RejRsnID || ord.FailureReason || '',
            thrFile: ord.ThrFile || ord.thrFile || ord.File || ord.file || ''
          }));

          if (ord.FdbkScore && Number(ord.FdbkScore) > 0) {
            setRating(Number(ord.FdbkScore));
            setHasExistingFeedback(true);
            setIsFeedbackSubmitted(true);
          }
          if (ord.FdbkNotes) {
            setFeedbackNotes(ord.FdbkNotes);
          }
          if (ord.RejRsnID || ord.FailureReason) {
            setSelectedReason(ord.RejRsnID || ord.FailureReason);
          }
        }
      } catch (err) {
        console.warn("Order fetch error:", err);
      }
    })();
  }, [aid, tid]);

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!message.trim()) return toast.error("Please enter your message.");
    if (!order.connID) return toast.error("Unable to send your message right now. Please try again later.");

    setIsSending(true);
    try {
      const result = await externalAPI.setFeedbackDetails({
        agencyID: Number(aid) || 0,
        connID: Number(order.connID) || 0,
        threadID: Number(tid) || 0,
        fdbkScore: 0,
        rejReason: '',
        fdbkNotes: message.trim()
      }, 'ORD');

      if (result?.error === false && result?.message === "Success") {
        setSentSuccess(true);
        toast.success("Your message has been sent to the supplier!");
        setMessage("");
        setTimeout(() => setSentSuccess(false), 4000);
      } else {
        toast.error(result?.message || "Failed to send message.");
      }
    } catch {
      toast.error("Failed to send message.");
    } finally {
      setIsSending(false);
    }
  };

  const handleStarClick = (score) => {
    if (hasExistingFeedback) return;
    setRating(score);
    if (score > 3) {
      setSelectedReason('');
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e?.preventDefault();
    if (rating === 0) {
      toast.error('Please select a star rating.');
      return;
    }
    if (!order.connID) {
      toast.error('Unable to submit feedback right now. Please try again later.');
      return;
    }

    setIsSubmittingFeedback(true);
    try {
      const isLow = rating > 0 && rating <= 3;
      const payload = {
        agencyID: Number(aid) || 0,
        connID: Number(order.connID) || 0,
        threadID: Number(tid) || 0,
        fdbkScore: rating,
        fdbkNotes: feedbackNotes.trim(),
        rejReason: isLow ? selectedReason : '',
        type: 'FDBK'
      };

      const result = await externalAPI.setFeedbackDetails(payload, 'FDBK');

      if (result?.error === false || result?.message === 'Success' || !result?.HasError) {
        setHasExistingFeedback(true);
        setIsFeedbackSubmitted(true);
        toast.success('Thank you! Your feedback has been submitted.');
      } else {
        toast.error(result?.message || 'Could not submit feedback. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting feedback:', err);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const isCompleted = order.statusID === 2 || Boolean(order.doneDT);
  const isLowRating = rating > 0 && rating <= 3;

  return (
    <div className="min-h-screen bg-[var(--bg-grey)] text-[var(--text-color)] font-sans flex flex-col justify-between p-4 sm:p-6 md:p-8">
      <div className="w-full max-w-7xl mx-auto space-y-4 flex-1">

        {/* Supplier Header: Compact minimal on Mobile, WebStore-matched on Desktop (lg:) */}
        <div className="bg-white border border-[var(--border)] rounded-xl lg:rounded-2xl py-3 px-4 sm:px-6 lg:p-7 shadow-xs lg:shadow-[0_2px_10px_rgba(0,0,0,0.04)] relative overflow-hidden flex flex-row items-center justify-between gap-4 lg:gap-8">
          {/* Brand: Logo on Left, Name & Tagline on Right */}
          <div className="flex flex-row items-center justify-start gap-3 lg:gap-5 min-w-0">
            {/* Logo Badge: Compact on mobile, WebStore badge container on desktop */}
            {supplier.logo ? (
              <div className="w-11 h-11 rounded-lg lg:w-20 lg:h-20 xl:w-22 xl:h-22 lg:rounded-2xl border border-[var(--border)] bg-white overflow-hidden flex items-center justify-center shrink-0">
                <img
                  src={supplier.logo}
                  alt={supplier.name}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            ) : (
              <div className="w-11 h-11 rounded-lg lg:w-20 lg:h-20 xl:w-22 xl:h-22 lg:rounded-2xl border border-[var(--border)] bg-[#0d1f2d] text-white flex items-center justify-center font-bold text-lg lg:text-3xl shrink-0">
                {supplier.logoText}
              </div>
            )}

            {/* Business Title & Tagline (tagline visible on desktop/tablet, wraps naturally) */}
            <div className="flex flex-col justify-center items-start text-left min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[var(--text-color)] tracking-tight truncate max-w-full">
                {supplier.name}
              </h1>
              {supplier.tagline && (
                <p className="hidden sm:block text-xs sm:text-sm lg:text-base text-[var(--sub-text-color)] font-normal leading-normal mt-0.5 max-w-3xl line-clamp-2">
                  {supplier.tagline}
                </p>
              )}
            </div>
          </div>

          {/* Action Icons: Compact icon-only on mobile, WebStore icon+label on desktop */}
          <div className="flex items-center justify-end gap-2 sm:gap-2.5 lg:gap-5 shrink-0">
            {supplier.phone && (
              <a
                href={`tel:${supplier.phone}`}
                className="flex flex-col items-center lg:gap-1.5 group cursor-pointer outline-none no-underline max-w-[72px] sm:max-w-[80px] lg:max-w-none"
                title="Call Business"
              >
                <div className="w-9 h-9 rounded-lg lg:w-12 lg:h-12 lg:rounded-xl bg-sahi-blue-light hover:opacity-90 text-sahi-dark-blue flex items-center justify-center transition-all active:scale-95 group-hover:shadow-sm">
                  <Phone className="h-4 w-4 lg:h-5 lg:w-5 text-sahi-dark-blue" />
                </div>
                <span className="hidden lg:block text-xs font-medium text-[var(--sub-text-color)] group-hover:text-[var(--text-color)] transition-colors">
                  Call
                </span>
              </a>
            )}

            {supplier.whatsapp && (
              <a
                href={`https://wa.me/${supplier.whatsapp}?text=Hello%2C%20regarding%20Order%20%23${order.number}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center lg:gap-1.5 group cursor-pointer outline-none no-underline max-w-[72px] sm:max-w-[80px] lg:max-w-none"
                title="WhatsApp"
              >
                <div className="w-9 h-9 rounded-lg lg:w-12 lg:h-12 lg:rounded-xl bg-[var(--sahi-green-light)] text-[var(--sahi-green)] flex items-center justify-center transition-all active:scale-95 group-hover:shadow-sm">
                  <BsWhatsapp className="h-4 w-4 lg:h-5 lg:w-5 text-[var(--sahi-green)]" />
                </div>
                <span className="hidden lg:block text-xs font-medium text-[var(--sub-text-color)] group-hover:text-[var(--text-color)] transition-colors">
                  WhatsApp
                </span>
              </a>
            )}
          </div>
        </div>

        {/* Content Layout: Single Column on Mobile, 8/4 Column Split on Desktop matching WebStore */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 items-start">

          {/* Left Column: 8 cols on desktop (WebStore main content ratio) */}
          <div className="lg:col-span-8 space-y-4 sm:space-y-6">

            {/* Order Details Card */}
            <div className="bg-white border border-[var(--border)] rounded-xl p-4 sm:p-6 shadow-xs space-y-4">

              {/* Order Details Header */}
              <div>
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
                  Sale <span className="text-[var(--sahi-green)]">#{order.number}</span>
                </h2>

                {!isCompleted && order.createdDT && (
                  <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs sm:text-sm">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[var(--bg-grey)] border border-[var(--border)]">
                      <Calendar className="w-3.5 h-3.5 text-[var(--sub-text-color)]" />
                      <span>Received on <span className="font-bold">{formatDateFull(order.createdDT)}</span></span>
                    </div>
                  </div>
                )}
              </div>

              {/* Order Information in Tabular Form without borders */}
              {isCompleted ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 items-start">
                  {/* Offering */}
                  <div className="min-w-0">
                    <span className="text-xs sm:text-sm font-semibold text-[var(--sub-text-color)] block mb-0.5">Offering</span>
                    <span className="font-semibold text-base sm:text-lg text-[var(--text-color)] break-words block">{order.item || '—'}</span>
                  </div>

                  {/* Received On */}
                  <div className="min-w-0">
                    <span className="text-xs sm:text-sm font-semibold text-[var(--sub-text-color)] block mb-0.5">Received on</span>
                    <span className="font-semibold text-base sm:text-lg text-[var(--text-color)] block truncate">
                      {order.createdDT ? format(new Date(order.createdDT), 'dd-MMM') : '—'}
                    </span>
                  </div>

                  {/* Completed On */}
                  <div className="min-w-0">
                    <span className="text-xs sm:text-sm font-semibold text-[var(--sub-text-color)] block mb-0.5">Completed on</span>
                    <span className="font-semibold text-base sm:text-lg text-[var(--text-color)] block truncate">
                      {order.doneDT ? format(new Date(order.doneDT), 'dd-MMM') : '—'}
                    </span>
                  </div>

                  {/* Amount */}
                  <div className="min-w-0 sm:text-right">
                    <span className="text-xs sm:text-sm font-semibold text-[var(--sub-text-color)] block mb-0.5">Amount</span>
                    <span className="font-semibold text-base sm:text-lg text-[var(--text-color)] block">
                      {order.amount ? `${currencySymbol} ${order.amount}` : '—'}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <span className="text-xs sm:text-sm font-semibold text-[var(--sub-text-color)] block mb-0.5">Offering</span>
                    <span className="font-semibold text-base sm:text-xl text-[var(--text-color)] break-words">{order.item || '—'}</span>
                  </div>
                  {order.amount && (
                    <div className="text-right shrink-0">
                      <span className="text-xs sm:text-sm font-semibold text-[var(--sub-text-color)] block mb-0.5">Amount</span>
                      <span className="font-semibold text-base sm:text-xl text-[var(--text-color)]">{currencySymbol} {order.amount}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Attached Document Section */}
              {order.thrFile && (
                <div className="pt-3.5 border-t border-[var(--border)] flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="p-2 rounded-lg border border-[var(--border)] shrink-0 text-[var(--sub-text-color)]">
                      <Paperclip className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <span className="text-base font-semibold text-[var(--text-color)] truncate block" title={String(order.thrFile).split('/').pop() || 'Attachment'}>
                        {String(order.thrFile).split('/').pop() || 'Attachment'}
                      </span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={() => setIsPreviewOpen(true)}
                    className="bg-sahi-green border hover:border-sahi-green hover:text-sahi-green shrink-0 h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3.5 sm:min-w-[100px]"
                    aria-label="Download Attachment"
                  >
                    <Download className="h-4 w-4" />
                    <span className="hidden sm:inline">Download</span>
                  </Button>
                </div>
              )}

              {/* Mobile-only Track Order (rendered inside same card on mobile only if timeline is present, hidden on lg:) */}
              {order.timeline && order.timeline.length > 0 && (
                <div className="border-t border-[var(--border)] pt-3.5 lg:hidden">
                  <h2 className="text-lg sm:text-xl font-bold tracking-tight mb-4">Track Sale</h2>

                  <div className="relative space-y-6">
                    {order.timeline.map((step, idx) => {
                      const isCompleted = step.status === "completed" || step.status === "done";
                      const isActive = step.status === "active";
                      const isLast = idx === order.timeline.length - 1;

                      return (
                        <div key={idx} className="relative flex items-start gap-3.5">
                          <div className="relative flex flex-col items-center shrink-0 w-8">
                            {!isLast && (
                              <div className="absolute top-8 bottom-[-24px] w-[2px] bg-[var(--sahi-green)] left-[calc(50%-1px)]" />
                            )}
                            <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 shadow-xs ${isCompleted
                              ? "bg-white text-[var(--sahi-green)] border-2 border-[var(--sahi-green)]"
                              : isActive
                                ? "bg-[var(--sahi-green)] text-white border-2 border-[var(--sahi-green)] ring-4 ring-[var(--sahi-green-light)]"
                                : "bg-white text-gray-400 border-2 border-gray-200"
                              }`}>
                              {isActive ? <Check className="w-4 h-4 stroke-[3]" /> : idx + 1}
                            </div>
                          </div>

                          <div className="flex-1 pt-1 min-w-0">
                            <div className="text-sm sm:text-base font-bold flex flex-wrap items-center gap-1.5">
                              <span>{step.date}</span>
                              <span className="text-[var(--sub-text-color)]">-</span>
                              <span className={isActive ? "text-[var(--sahi-green)]" : ""}>{step.title}</span>
                            </div>
                            {step.subtitle && <div className="text-xs sm:text-sm text-[var(--sub-text-color)] font-medium mt-0.5">{step.subtitle}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>

            {/* Feedback Card (if Order is Completed - StatusID === 2 or DoneDT) OR Send Message Card (if Ongoing) */}
            {isCompleted ? (
              <div className="bg-white border border-[var(--border)] rounded-xl p-4 sm:p-6 shadow-xs space-y-4">
                {isFeedbackSubmitted ? (
                  <div className="flex flex-col justify-center items-center py-6 sm:py-8 text-center">
                    <div className="flex items-center justify-center mx-auto mb-3 text-[var(--sahi-green)]">
                      <BadgeCheck className="w-12 h-12" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-[var(--text-color)] tracking-tight mb-1.5">
                      Thank You!
                    </h2>
                    <p className="text-sm sm:text-base text-[var(--sub-text-color)] leading-relaxed mb-4 max-w-sm">
                      Your feedback has been submitted successfully. It helps us continually improve our products and service standards.
                    </p>

                    {rating > 0 && (
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--sahi-green-light)] text-[var(--sahi-green)] text-xs sm:text-sm font-semibold mb-3">
                        <Star className="w-4 h-4 fill-[var(--sahi-green)] text-[var(--sahi-green)]" />
                        <span>Rated {rating} Star{rating > 1 ? 's' : ''} ({RATINGS_FEEDBACK_CONFIG[rating - 1]?.label})</span>
                      </div>
                    )}

                    {(rating === 4 || rating === 5) && supplier.googleLink && (
                      <div className="w-full mt-4 p-4 rounded-xl border border-[var(--border)] bg-gray-50/70 flex flex-col sm:flex-row items-center justify-between gap-3 text-left">
                        <div className="text-xs sm:text-sm text-[var(--text-color)]">
                          Would you like to share your review on <span className="font-semibold text-zinc-900">Google Reviews</span>?
                        </div>
                        <button
                          type="button"
                          onClick={() => window.open(supplier.googleLink, '_blank')}
                          className="w-full sm:w-auto h-9 px-3.5 bg-[var(--sahi-green)] text-white hover:bg-white hover:text-[var(--sahi-green)] border border-[var(--sahi-green)] rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 shadow-xs transition-colors shrink-0 cursor-pointer"
                        >
                          Google Reviews
                          <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4 sm:space-y-5">
                    {/* Header */}
                    <div className="text-left">
                      <h2 className="text-lg sm:text-xl font-bold tracking-tight text-[var(--text-color)]">
                        Feedback
                      </h2>
                      <p className="text-xs sm:text-sm text-[var(--sub-text-color)] mt-1">
                        Hi <span className="font-semibold text-[var(--text-color)]">{order.contactName || order.bizName || 'Customer'}</span>, please help us become your favourite supplier!
                      </p>
                    </div>

                    {/* Interactive Star Rating Section */}
                    <div className="space-y-2.5 text-left">
                      <label className="block text-sm sm:text-base font-medium text-[var(--text-color)]">
                        How do you feel about working with us?
                      </label>

                      <div className="flex items-center justify-start gap-3 sm:gap-4 py-0.5">
                        <div className="flex space-x-1 sm:space-x-1.5 -ml-1">
                          {[1, 2, 3, 4, 5].map((starIndex) => {
                            const currentScore = hoverRating || rating;
                            const activeColor = RATINGS_FEEDBACK_CONFIG[currentScore - 1]?.color;

                            return (
                              <button
                                key={starIndex}
                                type="button"
                                onClick={() => handleStarClick(starIndex)}
                                onMouseEnter={() => !hasExistingFeedback && setHoverRating(starIndex)}
                                onMouseLeave={() => !hasExistingFeedback && setHoverRating(0)}
                                disabled={isSubmittingFeedback || hasExistingFeedback}
                                className="transition-colors duration-200 focus:outline-none"
                              >
                                <Star
                                  fill="currentColor"
                                  className={`
                                    h-8 w-8 sm:h-10 sm:w-10
                                    ${starIndex <= currentScore
                                      ? `fill-${activeColor} text-${activeColor}`
                                      : 'text-gray-300'
                                    }
                                    hover:scale-110 transition-transform duration-200
                                    ${(isSubmittingFeedback || hasExistingFeedback) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                  `}
                                />
                              </button>
                            );
                          })}
                        </div>

                        {/* Show selected rating label */}
                        {(rating > 0 || hoverRating > 0) && (
                          <div>
                            <span className={`text-sm sm:text-base md:text-lg font-medium text-${RATINGS_FEEDBACK_CONFIG[(hoverRating || rating) - 1]?.color}`}>
                              {RATINGS_FEEDBACK_CONFIG[(hoverRating || rating) - 1]?.label}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Dynamic Failure Reason Chips for low ratings (1-3 stars) */}
                    {isLowRating && failureReasonsList.length > 0 && (
                      <div className="space-y-2 text-left animate-in fade-in duration-200">
                        <label className="block text-xs sm:text-sm font-medium text-[var(--text-color)]">
                          Please let us know what went wrong:
                        </label>
                        <div className="flex flex-wrap gap-1.5 sm:gap-2">
                          {failureReasonsList.map((reason) => {
                            const isSelected = selectedReason === reason.label || selectedReason === reason.id;
                            return (
                              <button
                                key={reason.id}
                                type="button"
                                onClick={() => setSelectedReason(prev => (prev === (reason.label || reason.id) ? '' : (reason.label || reason.id)))}
                                className={`h-9 px-3 text-xs font-normal rounded-lg border transition-all cursor-pointer select-none active:scale-[0.98] ${isSelected
                                  ? "border-[var(--sahi-green)] text-[var(--sahi-green)] bg-white shadow-xs"
                                  : "border-[var(--border)] text-[var(--text-color)] bg-white hover:bg-gray-100/80"
                                  }`}
                              >
                                {reason.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Feedback Textarea */}
                    <div className="relative text-left">
                      <Textarea
                        id="order-feedback-notes"
                        rows={3}
                        maxLength={500}
                        placeholder="Enter your feedback or suggestions..."
                        value={feedbackNotes}
                        onChange={(e) => setFeedbackNotes(e.target.value)}
                        className="text-sm sm:text-base border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg min-h-[100px] pr-20"
                        disabled={isSubmittingFeedback || hasExistingFeedback}
                      />
                      <SpeechControls
                        targetId="order-feedback-notes"
                        value={feedbackNotes}
                        onChange={(val) => setFeedbackNotes(val)}
                        className="absolute right-2.5 top-2.5"
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="button"
                      variant="sahiGreen"
                      onClick={handleFeedbackSubmit}
                      disabled={isSubmittingFeedback || rating === 0 || hasExistingFeedback}
                      className="w-full h-10 flex items-center justify-center gap-2 font-medium text-sm sm:text-base cursor-pointer rounded-lg shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmittingFeedback ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <span>Submit Feedback</span>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              /* Send Message Card for Active / In-progress Orders */
              <div className="bg-white border border-[var(--border)] rounded-xl p-4 sm:p-6 shadow-xs space-y-4">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold tracking-tight">
                    Send a message {supplier.name ? `to ${supplier.name}` : ''}
                  </h2>
                  <p className="text-xs sm:text-sm text-[var(--sub-text-color)] mt-1">Enter your message and we will get back to you.</p>
                </div>

                <form onSubmit={handleSendMessage} className="space-y-4">
                  <div className="relative text-left">
                    <Textarea
                      id="order-message-input"
                      rows={3}
                      required
                      maxLength={3980}
                      placeholder="Enter your message..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="text-sm sm:text-base border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg min-h-[100px] pr-20"
                      disabled={isSending}
                    />
                    <SpeechControls
                      targetId="order-message-input"
                      value={message}
                      onChange={(val) => setMessage(val)}
                      className="absolute right-2.5 top-2.5"
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="sahiGreen"
                    disabled={isSending || !message.trim()}
                    className="w-full h-10 flex items-center justify-center gap-2 font-medium text-sm sm:text-base cursor-pointer rounded-lg shadow-xs"
                  >
                    {isSending ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : sentSuccess ? (
                      <>
                        <Check className="w-5 h-5" />
                        <span>Message Sent</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        <span>Send Message</span>
                      </>
                    )}
                  </Button>
                </form>
              </div>
            )}

          </div>

          {/* Right Column: Track Order (Desktop only - 4 cols matching WebStore sidebar ratio) */}
          <div className="hidden lg:block lg:col-span-4 space-y-4 sm:space-y-6">
            <div className="bg-white border border-[var(--border)] rounded-xl lg:rounded-2xl p-6 shadow-xs">
              <h2 className="text-lg sm:text-xl font-bold tracking-tight mb-5">Track Sale</h2>

              {order.timeline && order.timeline.length > 0 ? (
                <div className="relative space-y-6">
                  {order.timeline.map((step, idx) => {
                    const isCompleted = step.status === "completed" || step.status === "done";
                    const isActive = step.status === "active";
                    const isLast = idx === order.timeline.length - 1;

                    return (
                      <div key={idx} className="relative flex items-start gap-3.5">
                        <div className="relative flex flex-col items-center shrink-0 w-8">
                          {!isLast && (
                            <div className="absolute top-8 bottom-[-24px] w-[2px] bg-[var(--sahi-green)] left-[calc(50%-1px)]" />
                          )}
                          <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 shadow-xs ${isCompleted
                            ? "bg-white text-[var(--sahi-green)] border-2 border-[var(--sahi-green)]"
                            : isActive
                              ? "bg-[var(--sahi-green)] text-white border-2 border-[var(--sahi-green)] ring-4 ring-[var(--sahi-green-light)]"
                              : "bg-white text-gray-400 border-2 border-gray-200"
                            }`}>
                            {isActive ? <Check className="w-4 h-4 stroke-[3]" /> : idx + 1}
                          </div>
                        </div>

                        <div className="flex-1 pt-1 min-w-0">
                          <div className="text-sm sm:text-base font-bold flex flex-wrap items-center gap-1.5">
                            <span>{step.date}</span>
                            <span className="text-[var(--sub-text-color)]">-</span>
                            <span className={isActive ? "text-[var(--sahi-green)]" : ""}>{step.title}</span>
                          </div>
                          {step.subtitle && <div className="text-xs sm:text-sm text-[var(--sub-text-color)] font-medium mt-0.5">{step.subtitle}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-8 px-4 bg-gray-50/60 rounded-xl border border-dashed border-gray-200 text-center flex flex-col items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-400 mb-2.5 shadow-xs">
                    <Clock className="w-5 h-5" />
                  </div>
                  <p className="text-sm font-semibold text-[var(--text-color)] mb-1">
                    No tracking updates shared yet
                  </p>
                  <p className="text-xs text-[var(--sub-text-color)] max-w-xs leading-relaxed">
                    The supplier has not added any progress updates for this order yet. Please check back soon for status updates.
                  </p>
                </div>
              )}
            </div>

            {/* Desktop Powered by */}
            <div className="flex items-center justify-center gap-1.5 pt-1 text-center select-none">
              <span className="text-xs text-[var(--sub-text-color)] font-medium tracking-wide">Powered by</span>
              <a href="https://sahi.club" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:opacity-85 transition-opacity" title="SahiClub">
                <img
                  src={getImageRoot('Images', 'SahiLogo.png')}
                  alt="SahiClub"
                  className="h-5 w-auto object-contain"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              </a>
            </div>
          </div>

        </div>

        {/* Mobile-only Powered by SahiClub */}
        <div className="flex lg:hidden items-center justify-center gap-1.5 pt-2 pb-4 text-center select-none">
          <span className="text-xs text-[var(--sub-text-color)] font-medium tracking-wide">Powered by</span>
          <a href="https://sahi.club" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:opacity-85 transition-opacity" title="SahiClub">
            <img
              src={getImageRoot('Images', 'SahiLogo.png')}
              alt="SahiClub"
              className="h-5 w-auto object-contain"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </a>
        </div>
      </div>

      {/* Attachment Preview Modal */}
      {order.thrFile && (
        <AttachmentPreviewModal
          open={isPreviewOpen}
          onOpenChange={setIsPreviewOpen}
          filePath={order.thrFile}
        />
      )}
    </div>
  );
};

export default OrderStatus;

